import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  LockReset as LockResetIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { SideDrawer, EmptyState } from './primitives';
import { isSchoolAdmin, isAdminRole, isTeacher, assignableRoles } from '../../constants/roles';
import { deriveStudentStatus, STATUS_META, formatLastActive } from './studentStatus';
import WeeklySchedule, { groupScheduleByDay } from './WeeklySchedule';
import TodaySchedule from './TodaySchedule';
import AccountSection from './AccountSection';
import ResetPasswordDialog from './ResetPasswordDialog';
import TransferSchoolDialog from './TransferSchoolDialog';
import { staffTierGuard } from './adminGuards';

// Student detail panel — GET /api/admin/users?username=<username> (an exact,
// case-insensitive match; there's no GET-by-id endpoint for a single user)
// plus GET /api/users/{id}/groups for the schedule/teacher detail. `username`
// (not `search`, which is an ILIKE substring match against username AND
// school) is what this drawer actually wants — a `search` match can be
// pushed past even a generously large page_size by other same-substring
// rows, which used to surface as a false "Student not found" here. Routed at
// /admin/students/:username by StudentsPage, which never unmounts across
// this drawer opening/closing — filters, sort, page, and scroll all live
// either in the URL query string or sessionStorage, untouched by the
// param change alone (see StudentsPage's useScrollRestore usage).
//
// Shares its Weekly Schedule rendering, Account (role/school/access) block,
// and one-time reset-password dialog with StaffDrawer.js (see
// WeeklySchedule.js/AccountSection.js/ResetPasswordDialog.js) — this file
// only owns what's actually student-specific: the status chip, the
// Classes add/remove list, and the "View conversations" footer action.
export default function StudentDrawer({
  username,
  onClose,
  groups,
  currentUserRole,
  currentUser,
  notify,
  confirmAction,
  onRoleChange,
  onSchoolChange,
  onAccessToggle,
  onDeleteUser,
  onResetPassword,
  onMutated,
}) {
  const navigate = useNavigate();
  const open = Boolean(username);

  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [user, setUser] = useState(null);

  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const [addClassOpen, setAddClassOpen] = useState(false);
  const [addClassTarget, setAddClassTarget] = useState('');
  const [addClassBusy, setAddClassBusy] = useState(false);
  const [removingGroupId, setRemovingGroupId] = useState(null);

  const [resetPasswordResult, setResetPasswordResult] = useState(null); // { user, temporaryPassword } | null
  const [passwordCopied, setPasswordCopied] = useState(false);

  const loadSchedule = useCallback(
    async (userId, signal) => {
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const res = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/groups`, {}, { signal });
        const data = await handleApiResponse(res, navigate);
        if (data === null || signal?.aborted) return; // 401 — already redirected
        setScheduleGroups(data.groups || []);
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted) return;
        setScheduleError(describeAdminError(err, "Couldn't load this student's classes."));
      } finally {
        if (!signal?.aborted) setScheduleLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!username) {
      setUser(null);
      setNotFound(false);
      setLoadError(null);
      setScheduleGroups([]);
      setAddClassOpen(false);
      return undefined;
    }
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(null);
      try {
        const params = new URLSearchParams({ username, page_size: '1', include: 'groups' });
        if (isTeacher(currentUserRole)) params.set('scope', 'my_classes');
        const res = await authFetch(`${config.API_BASE_URL}/api/admin/users?${params.toString()}`, {}, { signal: controller.signal });
        const data = await handleApiResponse(res, navigate);
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        const match = (data.users || [])[0] || null;
        if (!match) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setUser(match);
        setLoading(false);
        loadSchedule(match.id, controller.signal);
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setLoadError(describeAdminError(err, "Couldn't load this student."));
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [username, currentUserRole, navigate, loadSchedule]);

  const refreshUser = useCallback(async () => {
    if (!user) return;
    const controller = new AbortController();
    try {
      const params = new URLSearchParams({ username: user.username, page_size: '1', include: 'groups' });
      if (isTeacher(currentUserRole)) params.set('scope', 'my_classes');
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/users?${params.toString()}`, {}, { signal: controller.signal });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return;
      const match = (data.users || [])[0];
      if (match) setUser(match);
    } catch {
      // Best-effort refresh after a role/school/access edit — the toast
      // from that action already reported success/failure; a stale drawer
      // field until the next open isn't worth a second error surface.
    }
  }, [user, currentUserRole, navigate]);

  const status = user ? deriveStudentStatus(user) : null;
  const statusMeta = status ? STATUS_META[status] : null;
  const isAdminViewer = isAdminRole(currentUserRole);
  const pickableRoles = assignableRoles(currentUserRole);

  const scheduleByDay = useMemo(() => groupScheduleByDay(scheduleGroups), [scheduleGroups]);

  const addableClasses = useMemo(() => {
    if (!user) return [];
    const enrolledIds = new Set(scheduleGroups.map((g) => g.id));
    return (groups || [])
      .filter((g) => !enrolledIds.has(g.id))
      .filter((g) => {
        // SCHOOL_ADMIN/TEACHER's own `groups` prop is already scoped to
        // their school/classes server-side (GET /api/groups) — no need to
        // filter it again here. MASTER_ADMIN's spans every school, so it's
        // narrowed to the student's own school, per spec ("scoped to the
        // student's school").
        if (isTeacher(currentUserRole) || isSchoolAdmin(currentUserRole)) return true;
        return (g.school || '').trim().toLowerCase() === (user.school || '').trim().toLowerCase();
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, scheduleGroups, user, currentUserRole]);

  const handleAddClass = async () => {
    if (!addClassTarget || !user) return;
    setAddClassBusy(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${addClassTarget}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([user.id]),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      const className = addableClasses.find((g) => g.id === addClassTarget)?.name || 'the class';
      notify(`Added to ${className}.`, 'success');
      setAddClassOpen(false);
      setAddClassTarget('');
      await loadSchedule(user.id);
      onMutated?.();
    } catch (err) {
      notify(`Couldn't add to that class. ${describeAdminError(err)}`, 'error');
    } finally {
      setAddClassBusy(false);
    }
  };

  const performRemoveClass = async (group) => {
    if (!user) return;
    setRemovingGroupId(group.id);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${group.id}/members/bulk-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: [user.id] }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify(`Removed from ${group.name}.`, 'success');
      await loadSchedule(user.id);
      onMutated?.();
    } catch (err) {
      notify(`Couldn't remove from that class. ${describeAdminError(err)}`, 'error');
    } finally {
      setRemovingGroupId(null);
    }
  };

  const handleRemoveClass = (group) => {
    confirmAction({
      title: 'Remove from class',
      body: `Remove ${user.username} from "${group.name}"? If this was their only class covering the current time, they'll lose sign-in access until they're added to another one.`,
      confirmLabel: 'Remove',
      onConfirm: () => performRemoveClass(group),
    });
  };

  const handleRoleSelect = async (newRole) => {
    await onRoleChange(user.id, newRole);
    onMutated?.();
    refreshUser();
  };

  // Phase 4 (admin console v2) "smart transfer check" — unlike
  // AccountSection's default behavior (StaffDrawer's own copy of this
  // handler calls onSchoolChange the instant a new school is picked), a
  // student's SchoolPicker pick here doesn't write anything itself. It only
  // stages the target (`transferTarget`) and opens TransferSchoolDialog,
  // which previews the consequences via POST /users/{id}/transfer-check
  // before the admin confirms — see that component's own header comment.
  // Because AccountSection's SchoolPicker is a controlled input bound to
  // `user.school` (unchanged until a real PUT lands), simply not writing
  // anything here is enough for the Select to visually snap back to the
  // current value if the dialog is cancelled — no extra "revert" state
  // needed.
  const [transferTarget, setTransferTarget] = useState(null); // string ("" = Unassigned) | null (closed)

  const handleSchoolSelect = (newSchool) => {
    if ((newSchool || '') === (user.school || '')) return; // no-op: already there
    setTransferTarget(newSchool || '');
  };

  const closeTransferDialog = () => setTransferTarget(null);

  const handleTransferConfirmed = async (dropEnrollments) => {
    const ok = await onSchoolChange(user.id, transferTarget, { dropEnrollments });
    if (ok) {
      onMutated?.();
      refreshUser();
    }
    return ok;
  };

  const handleAccessClick = async () => {
    await onAccessToggle(user.id, user.access_granted !== false);
    onMutated?.();
    refreshUser();
  };

  const handleResetPasswordClick = () => {
    confirmAction({
      title: 'Reset Password',
      body: `Generate a temporary password for ${user.username}? Any existing reset links will stop working.`,
      confirmLabel: 'Generate Password',
      onConfirm: performResetPassword,
    });
  };

  const performResetPassword = async () => {
    try {
      const data = await onResetPassword(user.id);
      if (data === null) return; // 401 — already redirected
      setPasswordCopied(false);
      setResetPasswordResult({ user, temporaryPassword: data.temporary_password });
    } catch (err) {
      notify(`This password couldn't be reset. ${describeAdminError(err)}`, 'error');
    }
  };

  const closeResetPasswordResult = () => {
    setResetPasswordResult(null);
    setPasswordCopied(false);
  };

  const copyTemporaryPassword = async () => {
    const text = resetPasswordResult?.temporaryPassword || '';
    if (!text) return;
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(text);
      setPasswordCopied(true);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const succeeded = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (succeeded) setPasswordCopied(true);
        else notify('Copy failed — select the password above and copy it manually.', 'warning');
      } catch (fallbackErr) {
        console.error('Copy to clipboard failed:', fallbackErr);
        notify('Copy failed — select the password above and copy it manually.', 'warning');
      }
    }
  };

  const handleDeleteClick = () => {
    confirmAction({
      title: 'Delete User',
      body: `Delete user "${user.username}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        await onDeleteUser(user);
        onMutated?.();
        onClose();
      },
    });
  };

  const handleViewConversations = () => {
    navigate('/admin/conversations');
  };

  let body;
  if (loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (notFound) {
    body = (
      <EmptyState
        title="Student not found"
        description="They may have been removed, or you may not have access to them."
        action={
          <Button variant="outlined" onClick={onClose} sx={TOUCH_HIT_SX}>
            Back to Students
          </Button>
        }
      />
    );
  } else if (loadError) {
    body = <Alert severity="error">{loadError}</Alert>;
  } else if (user) {
    body = (
      <Box>
        {/* Header info: status, email, last active — SideDrawer's own
            title/subtitle already carry name + school. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, mb: 0.75 }}>
          <Chip
            label={statusMeta.label}
            size="small"
            sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700, height: 22, fontSize: '0.72rem' }}
          />
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            {user.email || 'No email on file'}
          </Typography>
        </Box>
        {/* Grade/Student ID (Phase 4, admin console v2) — both nullable
            free text per school (see User.grade/.student_id's docstring in
            app/models.py); this line simply doesn't render when a school
            hasn't recorded either one yet, rather than showing empty
            "Grade —" scaffolding. */}
        {(user.grade || user.student_id) && (
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 0.5 }}>
            {[user.grade && `Grade ${user.grade}`, user.student_id && `Student ID ${user.student_id}`]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block', mb: 3 }}>
          {formatLastActive(user.last_active)}
        </Typography>

        {/* Today (Phase 4, admin console v2) */}
        <TodaySchedule userId={user.id} />

        {/* Schedule */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <ScheduleIcon sx={{ fontSize: 18, color: adminColors.inkSoft }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>
            Weekly Schedule
          </Typography>
        </Box>

        <WeeklySchedule
          ownerName={user.username}
          scheduleByDay={scheduleByDay}
          loading={scheduleLoading}
          error={scheduleError}
          variant="student"
        />

        <Divider sx={{ mb: 3, borderColor: 'rgba(24,24,27,0.08)' }} />

        {/* Classes */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.25 }}>
          Classes
        </Typography>
        {scheduleLoading ? null : scheduleGroups.length === 0 ? (
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 1.5 }}>
            Not enrolled in any classes.
          </Typography>
        ) : (
          <List disablePadding sx={{ mb: 1 }}>
            {scheduleGroups.map((g) => (
              <ListItem
                key={g.id}
                disableGutters
                secondaryAction={
                  <Tooltip title={`Remove from ${g.name}`}>
                    <span>
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${user.username} from ${g.name}`}
                        onClick={() => handleRemoveClass(g)}
                        disabled={removingGroupId === g.id}
                        sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: '#dc2626' } }}
                      >
                        {removingGroupId === g.id ? <CircularProgress size={16} /> : <CloseIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
                sx={{ py: 0.75, px: 0 }}
              >
                <ListItemText
                  primary={g.name}
                  secondary={g.school || undefined}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: adminColors.ink }}
                  secondaryTypographyProps={{ variant: 'caption', color: adminColors.inkSoft }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Button
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setAddClassOpen((v) => !v)}
          sx={{ ...TOUCH_HIT_SX, fontWeight: 600, color: adminColors.accent, mb: 1 }}
        >
          Add to a class…
        </Button>
        <Collapse in={addClassOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, bgcolor: adminColors.surface, borderRadius: radius.control, mb: 2 }}>
            {addableClasses.length === 0 ? (
              <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                No other classes available at this school.
              </Typography>
            ) : (
              <FormControl size="small" fullWidth>
                <InputLabel id="drawer-add-class-label">Class</InputLabel>
                <Select
                  labelId="drawer-add-class-label"
                  label="Class"
                  value={addClassTarget}
                  onChange={(e) => setAddClassTarget(e.target.value)}
                  disabled={addClassBusy}
                >
                  {addableClasses.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setAddClassOpen(false)} disabled={addClassBusy} sx={TOUCH_HIT_SX}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAddClass}
                disabled={!addClassTarget || addClassBusy || addableClasses.length === 0}
                sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
              >
                {addClassBusy && <CircularProgress size={14} sx={{ mr: 1, color: 'white' }} />}
                Add
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Account — role/school/access, admins only */}
        {isAdminViewer && (
          <AccountSection
            user={user}
            currentUserRole={currentUserRole}
            currentUser={currentUser}
            pickableRoles={pickableRoles}
            onRoleSelect={handleRoleSelect}
            onSchoolSelect={handleSchoolSelect}
            onAccessClick={handleAccessClick}
          />
        )}
      </Box>
    );
  }

  return (
    <>
      <SideDrawer
        open={open}
        onClose={onClose}
        // Prefer the route param over `user.username`: switching directly
        // from one student's drawer to another's (browser back/forward
        // between two /admin/students/:username deep links, never passing
        // through the bare list) re-triggers the fetch but doesn't clear
        // `user` until the new one lands — without this, the header would
        // flash the *previous* student's name/school under the new
        // loading spinner.
        title={username || user?.username || 'Student'}
        subtitle={!loading && user ? user.school || 'No school assigned' : undefined}
        width={520}
        footer={
          // !loading guards against acting on a stale `user` (the previous
          // student) for the brief window between clicking a new
          // /admin/students/:username link and its fetch landing — see the
          // title/subtitle comment above for the same staleness source.
          !loading &&
          user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Button
                onClick={handleViewConversations}
                startIcon={<ChatIcon fontSize="small" />}
                sx={{ ...TOUCH_HIT_SX, fontWeight: 600, color: adminColors.accent }}
              >
                View conversations
              </Button>
              <Box sx={{ flex: 1 }} />
              {isAdminViewer && (
                <>
                  <Button
                    size="small"
                    onClick={handleResetPasswordClick}
                    startIcon={<LockResetIcon fontSize="small" />}
                    disabled={staffTierGuard(currentUserRole, user.role)}
                    sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft }}
                  >
                    Reset password
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleDeleteClick}
                    startIcon={<DeleteIcon fontSize="small" />}
                    disabled={staffTierGuard(currentUserRole, user.role)}
                    sx={TOUCH_HIT_SX}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          )
        }
      >
        {body}
      </SideDrawer>

      <ResetPasswordDialog
        username={resetPasswordResult?.user?.username}
        temporaryPassword={resetPasswordResult?.temporaryPassword}
        hasEmail={Boolean(resetPasswordResult?.user?.email)}
        copied={passwordCopied}
        onCopy={copyTemporaryPassword}
        onClose={closeResetPasswordResult}
      />

      <TransferSchoolDialog
        open={transferTarget !== null}
        onClose={closeTransferDialog}
        userId={user?.id}
        username={user?.username}
        currentSchoolName={user?.school}
        targetSchoolName={transferTarget}
        currentEnrollments={scheduleGroups}
        onConfirmed={handleTransferConfirmed}
      />
    </>
  );
}
